import * as React from 'react';
import { useEffect } from 'react';
import { PercentInput } from '../percent-input/PercentInput';
import Dropdown from '@app/react-common/components/dropdown/Dropdown';
import { ConfirmationModal } from '../confirmation-modal/ConfirmationModal';
import { WarningBanner } from '@app/react-common/components/banners/WarningBanner';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { AbTestTypeCategory } from '@app/pages/checkout-app/checkout-layout/main/components/ab-testing/react/models/AbTestCreationType';
import {
  TrafficAllocationVariant,
  TrafficAllocationConfig,
  TrafficAllocationFormData,
  matchesControlGroupType,
  findControlGroupTypeLabel,
} from './TrafficAllocationShared';

export interface TrafficAllocationDropdownProps {
  trafficAllocationConfig: TrafficAllocationConfig;
  onChange: (valid: boolean, variants: TrafficAllocationVariant[]) => void;
  viewMode?: boolean;
  showValidation?: boolean;
  shop: Shop;
  testTypeCategory?: AbTestTypeCategory;
  placeholder?: string;
}

function buildDropdownOptionsForSlot(
  variants: TrafficAllocationVariant[],
  slotIndex: number,
  controlGroupTypeLabel: string | null,
) {
  return variants
    .filter((variant) => slotIndex === 0 || matchesControlGroupType(variant, controlGroupTypeLabel))
    .map((variant) => ({
      label: variant.name,
      value: variant.internalId,
      customContent: variant.groupTypeLabel ? (
        <div className="traffic-allocation-dropdown__option">
          <span className="traffic-allocation-dropdown__option__name">{variant.name}</span>
          <span className="traffic-allocation-dropdown__option__group-type">{variant.groupTypeLabel}</span>
        </div>
      ) : undefined,
    }));
}

export const TrafficAllocationDropdown: React.FC<TrafficAllocationDropdownProps> = ({
  trafficAllocationConfig,
  onChange,
  viewMode = false,
  showValidation,
  shop,
  testTypeCategory,
  placeholder,
}) => {
  const [validationMessageForModal, setValidationMessageForModal] = React.useState<string>('');
  const [formData, setFormData] = React.useState<TrafficAllocationFormData>({
    variants: trafficAllocationConfig.variants.map((variant, idx) => {
      const hasExistingSelections = trafficAllocationConfig.variants.some((v) => v.isSelected);

      // Determine slot index for existing selections
      let slotIndex = variant.slotIndex;
      if (hasExistingSelections && variant.isSelected && slotIndex === undefined) {
        const selectedVariants = trafficAllocationConfig.variants.filter((v) => v.isSelected);
        slotIndex = selectedVariants.indexOf(variant);
      }

      return {
        ...variant,
        internalId: idx,
        isSelected: !hasExistingSelections ? false : variant.isSelected,
        percentageTraffic: !hasExistingSelections ? 0 : variant.percentageTraffic,
        slotIndex: variant.isSelected ? slotIndex : undefined,
      };
    }),
  });

  // Update internal state when trafficAllocationConfig changes from parent
  useEffect(() => {
    const currentVariants = trafficAllocationConfig.variants;

    setFormData((prevFormData) => {
      // Check if our internal state already matches the new config to avoid unnecessary updates
      const internalMatchesConfig =
        prevFormData.variants.length === currentVariants.length &&
        prevFormData.variants.every((internalVariant) => {
          const configVariant = currentVariants.find((v) => v.widgetExternalId === internalVariant.widgetExternalId);
          if (!configVariant) return false;
          return (
            internalVariant.isSelected === configVariant.isSelected &&
            internalVariant.slotIndex === configVariant.slotIndex &&
            internalVariant.percentageTraffic === configVariant.percentageTraffic
          );
        });

      if (internalMatchesConfig) {
        return prevFormData; // No change needed
      }

      return {
        variants: currentVariants.map((variant, idx) => {
          // Preserve existing internalId if available, otherwise use index
          const existingVariant = prevFormData.variants.find((v) => v.widgetExternalId === variant.widgetExternalId);
          const internalId = existingVariant?.internalId ?? idx;

          const hasExistingSelections = currentVariants.some((v) => v.isSelected);

          // Determine slot index for existing selections
          let slotIndex = variant.slotIndex;
          if (hasExistingSelections && variant.isSelected && slotIndex === undefined) {
            const selectedVariants = currentVariants.filter((v) => v.isSelected);
            slotIndex = selectedVariants.findIndex((v) => v.widgetExternalId === variant.widgetExternalId);
          }

          return {
            ...variant,
            internalId: internalId,
            isSelected: !hasExistingSelections ? false : variant.isSelected,
            percentageTraffic: !hasExistingSelections ? 0 : variant.percentageTraffic,
            slotIndex: variant.isSelected ? slotIndex : undefined,
          };
        }),
      };
    });
  }, [trafficAllocationConfig.variants]);

  // Validate and notify parent of changes whenever formData changes
  useEffect(() => {
    const selectedVariants = formData.variants.filter((v) => v.isSelected);
    // For EXTENSIONS_GROUPS_TEST, allow 2 or more groups. For others, require exactly 2.
    const hasMinimumGroups =
      testTypeCategory === AbTestTypeCategory.EXTENSIONS_GROUPS
        ? selectedVariants.length >= 2
        : selectedVariants.length === 2;

    // For extension groups with more than 2, validate that percentages sum to 100%
    let percentageSumValid = true;
    if (testTypeCategory === AbTestTypeCategory.EXTENSIONS_GROUPS && selectedVariants.length > 2) {
      const percentageSum = selectedVariants.reduce((sum, v) => sum + v.percentageTraffic, 0);
      percentageSumValid = percentageSum === 100;
    }

    const isValid = hasMinimumGroups && percentageSumValid;

    // Mark that this is an internal update to prevent circular updates
    onChange(isValid, formData.variants);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, testTypeCategory]);

  const selectedVariants = formData.variants.filter((v) => v.isSelected);
  const isExtensionGroupsTest = testTypeCategory === AbTestTypeCategory.EXTENSIONS_GROUPS;

  const getGroupTitle = (slotIndex: number): string => {
    return slotIndex === 0 ? 'Control group' : 'Test group';
  };

  // Get all slot indices that have selected variants
  const getSlotIndices = (): number[] => {
    if (isExtensionGroupsTest) {
      const slotIndices = selectedVariants
        .filter((v) => v.slotIndex !== undefined && v.slotIndex >= 0)
        .map((v) => v.slotIndex as number)
        .sort((a, b) => a - b);
      // Always show at least Control group (0) and one Test group (1)
      // If we have selected variants, show all their slot indices
      if (slotIndices.length > 0) {
        // Ensure we have slot 0 (Control group) if any variant is selected
        if (!slotIndices.includes(0) && selectedVariants.length > 0) {
          slotIndices.unshift(0);
        }
        return slotIndices;
      }
      return [0, 1];
    }
    return [0, 1];
  };
  const setVariantPercentage = (id: number, percentage: number) => {
    setFormData((oldFormData) => {
      const selectedVariants = oldFormData.variants.filter((v) => v.isSelected);
      const isExtensionGroups = testTypeCategory === AbTestTypeCategory.EXTENSIONS_GROUPS;

      // Clamp percentage to valid range (0-100)
      let clampedPercentage = Math.max(0, Math.min(100, percentage));

      if (isExtensionGroups && selectedVariants.length > 2) {
        // For extension groups with multiple test groups, allow individual percentage setting
        // Prevent increments that would make total exceed 100, but allow decrements
        const currentVariant = oldFormData.variants.find((v) => v.internalId === id);
        const otherVariantsTotal = selectedVariants
          .filter((v) => v.internalId !== id)
          .reduce((sum, v) => sum + v.percentageTraffic, 0);

        // If incrementing (new value > current value) and total would exceed 100, prevent it
        const isIncrement = currentVariant && clampedPercentage > currentVariant.percentageTraffic;
        if (isIncrement && otherVariantsTotal + clampedPercentage > 100) {
          // Allow up to what would make total = 100
          clampedPercentage = Math.max(currentVariant.percentageTraffic, 100 - otherVariantsTotal);
        }

        return {
          variants: oldFormData.variants.map((variant) => {
            if (variant.internalId === id) {
              return {
                ...variant,
                percentageTraffic: clampedPercentage,
              };
            }
            return variant;
          }),
        };
      } else {
        // For 2 groups, maintain the 100% split
        return {
          variants: oldFormData.variants.map((variant) => {
            if (variant.internalId === id) {
              return {
                ...variant,
                percentageTraffic: clampedPercentage,
              };
            }
            if (selectedVariants.length === 2 && variant.isSelected && variant.widgetExternalId !== id) {
              return {
                ...variant,
                percentageTraffic: 100 - clampedPercentage,
              };
            }
            return variant;
          }),
        };
      }
    });
  };

  const handleRemoveVariant = (slotIndex: number) => {
    if (!isExtensionGroupsTest) return;

    setFormData((oldFormData) => {
      const selectedVariants = oldFormData.variants.filter((v) => v.isSelected);

      // Prevent removing if there are only 2 variants (minimum required)
      if (selectedVariants.length <= 2) {
        return oldFormData;
      }

      const variantToRemove = oldFormData.variants.find((v) => v.isSelected && v.slotIndex === slotIndex);
      if (!variantToRemove) return oldFormData;

      const remainingSelectedVariants = oldFormData.variants.filter((v) => v.isSelected && v.slotIndex !== slotIndex);
      const newSelectedCount = remainingSelectedVariants.length;

      // Redistribute percentages evenly among remaining variants
      const newPercentage = newSelectedCount > 0 ? Math.floor(100 / newSelectedCount) : 0;

      return {
        variants: oldFormData.variants.map((variant) => {
          // Remove the variant from this slot
          if (variant.internalId === variantToRemove.internalId) {
            return {
              ...variant,
              isSelected: false,
              slotIndex: undefined,
              percentageTraffic: 0,
            };
          }

          // Redistribute percentages for remaining selected variants
          if (variant.isSelected && variant.slotIndex !== slotIndex) {
            return {
              ...variant,
              percentageTraffic: newPercentage,
            };
          }

          return variant;
        }),
      };
    });
  };

  const handleFixedDropdownSelect = (slotIndex: number, selectedVariantId: number) => {
    const selectedVariant = formData.variants.find((v) => v.internalId === selectedVariantId);
    if (!selectedVariant) return;

    const isExtensionGroups = testTypeCategory === AbTestTypeCategory.EXTENSIONS_GROUPS;

    // Find what's currently in each slot
    const variantInCurrentSlot = formData.variants.find((v) => v.isSelected && v.slotIndex === slotIndex);

    // Check if trying to select a variant that's already in another slot
    const variantInOtherSlot = formData.variants.find(
      (v) => v.isSelected && v.slotIndex !== slotIndex && v.internalId === selectedVariantId,
    );

    if (variantInOtherSlot) {
      setValidationMessageForModal('This extension group is already selected in another slot.');
      return;
    }

    setFormData((oldFormData) => {
      const mismatchedVariantIds = new Set(
        slotIndex === 0
          ? oldFormData.variants
              .filter(
                (v) =>
                  v.isSelected &&
                  v.internalId !== undefined &&
                  v.slotIndex !== slotIndex &&
                  !matchesControlGroupType(v, selectedVariant.groupTypeLabel ?? null),
              )
              .map((v) => v.internalId)
          : [],
      );
      const isMismatched = (variant: TrafficAllocationVariant) =>
        variant.internalId !== undefined && mismatchedVariantIds.has(variant.internalId);

      const selectedVariants = oldFormData.variants.filter((v) => v.isSelected && !isMismatched(v));
      const selectedCount = selectedVariants.length;
      const isReplacing = variantInCurrentSlot !== undefined;
      const newSelectedCount = isReplacing ? selectedCount : selectedCount + 1;

      // Calculate percentage: for extension groups with multiple groups, distribute evenly
      // For 2 groups, use 50/50 split
      const percentage = isExtensionGroups && newSelectedCount > 2 ? Math.floor(100 / newSelectedCount) : 50;

      return {
        variants: oldFormData.variants.map((variant) => {
          if (isMismatched(variant)) {
            return { ...variant, isSelected: false, slotIndex: undefined, percentageTraffic: 0 };
          }

          // If this is the variant being selected for the current slot
          if (variant.internalId === selectedVariantId) {
            return {
              ...variant,
              isSelected: true,
              slotIndex: slotIndex,
              percentageTraffic: percentage,
            };
          }

          // If this is the variant currently in the current slot (being replaced)
          if (variantInCurrentSlot && variant.internalId === variantInCurrentSlot.internalId) {
            return {
              ...variant,
              isSelected: false,
              slotIndex: undefined,
              percentageTraffic: 0,
            };
          }

          // For extension groups with multiple groups, redistribute percentages
          if (isExtensionGroups && variant.isSelected && variant.slotIndex !== slotIndex) {
            return {
              ...variant,
              percentageTraffic: Math.floor(100 / newSelectedCount),
            };
          }

          return variant;
        }),
      };
    });
  };

  const showExtensionTypeMismatchWarning = React.useMemo(() => {
    if (selectedVariants.length !== 2) return false;
    const [variant1, variant2] = selectedVariants;

    if (variant1.variantType && variant2.variantType) {
      return variant1.variantType !== variant2.variantType;
    }
    return false;
  }, [selectedVariants]);

  const showPercentageValidationError = React.useMemo(() => {
    if (!isExtensionGroupsTest || selectedVariants.length <= 2) return false;

    const percentageSum = selectedVariants.reduce((sum, v) => sum + v.percentageTraffic, 0);
    return percentageSum !== 100;
  }, [selectedVariants, isExtensionGroupsTest]);

  const percentageValidationMessage = React.useMemo(() => {
    if (!showPercentageValidationError) return '';

    const percentageSum = selectedVariants.reduce((sum, v) => sum + v.percentageTraffic, 0);
    if (percentageSum < 100) {
      return `Traffic allocation percentages must add up to 100%. Current total: ${percentageSum}%`;
    } else {
      return `Traffic allocation percentages must add up to 100%. Current total: ${percentageSum}%`;
    }
  }, [showPercentageValidationError, selectedVariants]);

  return (
    <div className="traffic-allocation-dropdown">
      {showExtensionTypeMismatchWarning && testTypeCategory === AbTestTypeCategory.EXTENSIONS && (
        <WarningBanner
          title="Pay attention"
          content="Extensions from different type are selected two Shopify blocks will be used"
          hideCloseButton={true}
          variant="attention"
        />
      )}
      {showPercentageValidationError && showValidation && (
        <WarningBanner
          title="Invalid traffic allocation"
          content={percentageValidationMessage}
          hideCloseButton={true}
          variant="error"
        />
      )}
      {getSlotIndices().map((slotIndex) => {
        const selectedVariant = formData.variants.find((v) => v.isSelected && v.slotIndex === slotIndex) || null;
        // Filter variants: NONE option available for all groups in EXTENSIONS tests, but only Control group for EXTENSIONS_GROUPS tests
        const filteredVariants = formData.variants.filter((variant) => {
          const isNone = variant.widgetExternalId === null;
          // For EXTENSIONS tests, show NONE for all groups. For EXTENSIONS_GROUPS, only show for Control group
          if (testTypeCategory === AbTestTypeCategory.EXTENSIONS) {
            return true; // Show all variants including NONE for all groups in extension tests
          }
          // For other test types (EXTENSIONS_GROUPS, etc.), show NONE only for Control group
          return slotIndex === 0 || !isNone;
        });
        const availableOptions = buildDropdownOptionsForSlot(
          filteredVariants,
          slotIndex,
          findControlGroupTypeLabel(formData.variants),
        );

        return (
          <div key={slotIndex} className="traffic-allocation-dropdown__item">
            <div className="traffic-allocation-dropdown__item__label">{getGroupTitle(slotIndex)}</div>
            <div className="traffic-allocation-dropdown__item__controls">
              <div className="traffic-allocation-dropdown__item__selection">
                <Dropdown
                  options={availableOptions}
                  onSelect={(option) => {
                    if (!viewMode && option) {
                      handleFixedDropdownSelect(slotIndex, option.value);
                    }
                  }}
                  preSelectedOption={
                    selectedVariant
                      ? {
                          label: selectedVariant.name,
                          value: selectedVariant.internalId,
                        }
                      : undefined
                  }
                  placeholder={placeholder}
                  disabled={viewMode}
                />
              </div>
              <div className="traffic-allocation-dropdown__item__percentage">
                <PercentInput
                  value={selectedVariant?.percentageTraffic || 0}
                  setValue={(value) => {
                    if (!viewMode && selectedVariant) {
                      setVariantPercentage(selectedVariant.internalId, value);
                    }
                  }}
                  disabled={viewMode || !selectedVariant}
                  setValid={() => {}}
                  viewMode={viewMode}
                />
              </div>
              {isExtensionGroupsTest && selectedVariant && !viewMode && slotIndex !== 0 && slotIndex !== 1 && (
                <div
                  onClick={() => handleRemoveVariant(slotIndex)}
                  className="traffic-allocation-dropdown__remove-button">
                  <i className="icon icon-close traffic-allocation-dropdown__remove-icon"></i>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {validationMessageForModal && (
        <ConfirmationModal
          title={validationMessageForModal}
          buttonText={'Ok'}
          isOpen={!!validationMessageForModal}
          mainContent={'Please select a different extension.'}
          onClose={() => {
            setValidationMessageForModal('');
          }}
          onSuccess={() => {
            setValidationMessageForModal('');
          }}
          hideCancel={true}
        />
      )}
    </div>
  );
};
